import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

type FormState = {
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
  country: string;
  website: string;
  category: string;
  productTypes: string;
  minOrderValue: string;
  annualRevenue: string;
  warehouseInterest: string;
  certifications: string;
  message: string;
};

const initialForm: FormState = {
  companyName: "",
  contactName: "",
  email: "",
  phone: "",
  country: "",
  website: "",
  category: "",
  productTypes: "",
  minOrderValue: "",
  annualRevenue: "",
  warehouseInterest: "",
  certifications: "",
  message: "",
};

const SupplierRegisterPage = () => {
  const [form, setForm] = useState<FormState>(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Become a Verified Supplier – Sell to USA & Canada Buyers | Crown Prime Mall";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "Apply to become a verified factory supplier on Crown Prime Mall. Reach 50,000+ active buyers across USA and Canada. Get the Crown Verified badge and grow your export business.");
    }

    const siteUrl = import.meta.env.VITE_SITE_URL || "https://example.com";
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "schema-supplier-register";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Become a Verified Supplier – Crown Prime Mall",
      "description": "Apply to become a verified factory supplier on Crown Prime Mall. Reach 50,000+ active buyers across USA and Canada. List your products and grow your export business.",
      "url": siteUrl + "/supplier-register",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": siteUrl },
          { "@type": "ListItem", "position": 2, "name": "Supplier Registration", "item": siteUrl + "/supplier-register" }
        ]
      },
      "mainEntity": {
        "@type": "Organization",
        "name": "Crown Prime Mall",
        "url": siteUrl,
        "description": "Crown Prime Mall connects verified factory suppliers with buyers across USA and Canada."
      }
    });
    if (!document.getElementById("schema-supplier-register")) {
      document.head.appendChild(script);
    }
    return () => {
      const el = document.getElementById("schema-supplier-register");
      if (el) el.remove();
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (form.message.length > 500) {
      setError("Message must be 500 characters or less.");
      return;
    }
    setLoading(true);
    setError("");
    const formData = new URLSearchParams();
    Object.entries(form).forEach(([key, val]) => formData.append(key, val));
    try {
      await fetch("https://readdy.ai/api/form/d7dcrkckpdh0a4advve0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });
      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again or contact us via WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fdf8f8]">
      {/* Top Bar */}
      <div className="bg-[#1a0a0a] text-white text-xs text-center py-2 px-4">
        FREE SHIPPING on orders over $50 &nbsp;|&nbsp; Secure USD Payments &bull; Verified Suppliers
      </div>

      {/* Navbar */}
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-[1400px] mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 cursor-pointer whitespace-nowrap flex-shrink-0">
            <img
              src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b31ed3570265bb91107b593ba40da4ad.jpeg"
              alt="Crown Prime Mall"
              className="h-9 w-9 rounded-full object-cover"
            />
            <div>
              <div className="font-bold text-sm text-[#C41E3A] leading-tight">Crown Prime Mall</div>
              <div className="text-[10px] text-gray-400">USA &amp; Canada Marketplace</div>
            </div>
          </Link>
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <Link to="/" className="hover:text-[#C41E3A] cursor-pointer whitespace-nowrap">Home</Link>
            <span>/</span>
            <span className="text-gray-800 font-medium">Supplier Registration</span>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="bg-gradient-to-r from-[#C41E3A] to-[#8B0000] py-12">
        <div className="max-w-[1400px] mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-vip-crown-line text-[#D4AF37] text-xl"></i>
              </div>
              <span className="text-[#D4AF37] text-sm font-semibold uppercase tracking-wider">Factory Supplier Program</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-white mb-2">Become a Verified Supplier</h1>
            <p className="text-white/70 text-sm max-w-xl">
              Reach thousands of USA &amp; Canada buyers, resellers, and influencers. List your products on Crown Prime Mall and grow your export business.
            </p>
          </div>
          <div className="flex flex-wrap gap-4">
            {[
              { icon: "ri-user-star-line", label: "50K+ Active Buyers" },
              { icon: "ri-map-2-line", label: "USA & Canada Markets" },
              { icon: "ri-shield-check-line", label: "Verified Badge" },
            ].map((b) => (
              <div key={b.label} className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-3">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${b.icon} text-[#D4AF37]`}></i>
                </div>
                <span className="text-white text-xs font-semibold whitespace-nowrap">{b.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-[900px] mx-auto px-6 py-12">
        {submitted ? (
          <div className="bg-white rounded-lg border border-gray-100 p-12 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
              <i className="ri-check-line text-green-500 text-3xl"></i>
            </div>
            <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Application Submitted!</h2>
            <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
              Thank you for applying to Crown Prime Mall. Our team will review your application and contact you within 2-3 business days.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <a
                href="https://wa.me/1234567890"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-[#25D366] text-white px-5 py-3 rounded-full font-semibold text-sm hover:bg-[#1ebe5d] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line"></i> Follow up on WhatsApp
              </a>
              <Link
                to="/"
                className="flex items-center gap-2 border-2 border-[#C41E3A] text-[#C41E3A] px-5 py-3 rounded-full font-semibold text-sm hover:bg-[#C41E3A] hover:text-white transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-home-line"></i> Back to Home
              </Link>
            </div>
          </div>
        ) : (
          <form
            data-readdy-form
            id="supplier-registration-form"
            onSubmit={handleSubmit}
            className="bg-white rounded-lg border border-gray-100 p-8"
          >
            <h2 className="text-xl font-extrabold text-gray-900 mb-1">Supplier Application Form</h2>
            <p className="text-gray-400 text-sm mb-8">All fields marked with * are required.</p>

            {/* Company Info */}
            <div className="mb-8">
              <h3 className="text-sm font-bold text-[#C41E3A] uppercase tracking-wider mb-4 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center"><i className="ri-building-2-line"></i></div>
                Company Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Company Name *</label>
                  <input
                    type="text"
                    name="companyName"
                    value={form.companyName}
                    onChange={handleChange}
                    required
                    placeholder="e.g. ShenZhen ElectroPro Co."
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#C41E3A] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Contact Person *</label>
                  <input
                    type="text"
                    name="contactName"
                    value={form.contactName}
                    onChange={handleChange}
                    required
                    placeholder="Full name"
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#C41E3A] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    placeholder="company@example.com"
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#C41E3A] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Phone / WhatsApp *</label>
                  <input
                    type="text"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    required
                    placeholder="+1 or +86..."
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#C41E3A] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Country *</label>
                  <select
                    name="country"
                    value={form.country}
                    onChange={handleChange}
                    required
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#C41E3A] transition-colors bg-white cursor-pointer"
                  >
                    <option value="">Select country</option>
                    <option>China</option>
                    <option>USA</option>
                    <option>Canada</option>
                    <option>India</option>
                    <option>Vietnam</option>
                    <option>Bangladesh</option>
                    <option>Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Company Website</label>
                  <input
                    type="text"
                    name="website"
                    value={form.website}
                    onChange={handleChange}
                    placeholder="https://yourcompany.com"
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#C41E3A] transition-colors"
                  />
                </div>
              </div>
            </div>

            {/* Product Info */}
            <div className="mb-8">
              <h3 className="text-sm font-bold text-[#C41E3A] uppercase tracking-wider mb-4 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center"><i className="ri-store-2-line"></i></div>
                Product Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Main Category *</label>
                  <select
                    name="category"
                    value={form.category}
                    onChange={handleChange}
                    required
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#C41E3A] transition-colors bg-white cursor-pointer"
                  >
                    <option value="">Select category</option>
                    <option>Electronics</option>
                    <option>Fashion &amp; Apparel</option>
                    <option>Home Goods</option>
                    <option>Beauty &amp; Cosmetics</option>
                    <option>Sports &amp; Fitness</option>
                    <option>Toys &amp; Kids</option>
                    <option>Food &amp; Beverage</option>
                    <option>Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Minimum Order Value *</label>
                  <select
                    name="minOrderValue"
                    value={form.minOrderValue}
                    onChange={handleChange}
                    required
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#C41E3A] transition-colors bg-white cursor-pointer"
                  >
                    <option value="">Select range</option>
                    <option>Under $100</option>
                    <option>$100 - $500</option>
                    <option>$500 - $2,000</option>
                    <option>$2,000 - $10,000</option>
                    <option>Over $10,000</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Annual Export Revenue</label>
                  <select
                    name="annualRevenue"
                    value={form.annualRevenue}
                    onChange={handleChange}
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#C41E3A] transition-colors bg-white cursor-pointer"
                  >
                    <option value="">Select range</option>
                    <option>Under $100K</option>
                    <option>$100K - $500K</option>
                    <option>$500K - $2M</option>
                    <option>Over $2M</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Warehouse Interest *</label>
                  <select
                    name="warehouseInterest"
                    value={form.warehouseInterest}
                    onChange={handleChange}
                    required
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#C41E3A] transition-colors bg-white cursor-pointer"
                  >
                    <option value="">Select option</option>
                    <option>USA Warehouse Only</option>
                    <option>Canada Warehouse Only</option>
                    <option>Both USA &amp; Canada</option>
                    <option>Direct from Factory (No Warehouse)</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Product Types / Description *</label>
                  <input
                    type="text"
                    name="productTypes"
                    value={form.productTypes}
                    onChange={handleChange}
                    required
                    placeholder="e.g. Wireless earbuds, smart home devices, LED lighting..."
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#C41E3A] transition-colors"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Certifications (FDA, CE, ASTM, etc.)</label>
                  <input
                    type="text"
                    name="certifications"
                    value={form.certifications}
                    onChange={handleChange}
                    placeholder="e.g. CE, RoHS, FDA, ISO 9001..."
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#C41E3A] transition-colors"
                  />
                </div>
              </div>
            </div>

            {/* Message */}
            <div className="mb-8">
              <h3 className="text-sm font-bold text-[#C41E3A] uppercase tracking-wider mb-4 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center"><i className="ri-message-3-line"></i></div>
                Additional Message
              </h3>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                maxLength={500}
                rows={4}
                placeholder="Tell us more about your factory, products, and why you want to join Crown Prime Mall..."
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#C41E3A] transition-colors resize-none"
              />
              <div className="text-right text-xs text-gray-400 mt-1">{form.message.length}/500</div>
            </div>

            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <div className="flex flex-wrap gap-3 items-center justify-between">
              <p className="text-xs text-gray-400">
                By submitting, you agree to our <a href="#" className="text-[#C41E3A] hover:underline">Terms of Service</a> and <a href="#" className="text-[#C41E3A] hover:underline">Privacy Policy</a>.
              </p>
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 bg-[#C41E3A] text-white px-8 py-3 rounded-full font-bold text-sm hover:bg-[#a01830] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {loading ? (
                  <><i className="ri-loader-4-line animate-spin"></i> Submitting...</>
                ) : (
                  <><i className="ri-send-plane-line"></i> Submit Application</>
                )}
              </button>
            </div>
          </form>
        )}

        {/* Side Info */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { icon: "ri-time-line", title: "Fast Review", desc: "Applications reviewed within 2-3 business days" },
            { icon: "ri-whatsapp-line", title: "WhatsApp Support", desc: "Chat with our supplier team directly" },
            { icon: "ri-shield-check-line", title: "Verified Badge", desc: "Get the Crown Verified Supplier badge" },
          ].map((item) => (
            <div key={item.title} className="bg-white rounded-lg border border-gray-100 p-5 flex gap-3 items-start">
              <div className="w-8 h-8 flex items-center justify-center bg-[#C41E3A]/10 rounded-lg flex-shrink-0">
                <i className={`${item.icon} text-[#C41E3A]`}></i>
              </div>
              <div>
                <div className="font-semibold text-gray-900 text-sm">{item.title}</div>
                <div className="text-xs text-gray-400 mt-0.5">{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SupplierRegisterPage;
